import { apiRequest } from '../../shared/api/client';

/**
 * Reference / dropdown data — mirrors Invetrak.Api.Endpoints.LookupEndpoints and
 * Invetrak.Infrastructure.Lookups.LookupTypes (camelCase on the wire).
 *
 *   GET /api/v1/uoms            → UomDto[]
 *   GET /api/v1/item-types      → ItemTypeDto[]
 *   GET /api/v1/location-types  → LocationTypeDto[]
 *   GET /api/v1/balance-types   → BalanceTypeDto[]
 *   GET /api/v1/countries       → CountryDto[]
 *   GET /api/v1/state-provinces → StateProvinceDto[]   ?countryCode= for one country's
 *
 * These need only an authenticated session — they're per-tenant constants, not
 * business data, so there's no permission check server-side.
 */

export interface UomDto {
  uomId: string;
  uomCode: string;
  displayName: string;
  /** count / weight / volume / length / area / time */
  uomType: string;
  isActive: boolean;
}

export interface ItemTypeDto {
  itemTypeId: string;
  itemTypeCode: string;
  displayName: string;
  isActive: boolean;
}

export interface LocationTypeDto {
  locationTypeId: string;
  locationTypeCode: string;
  displayName: string;
  isPickable: boolean;
  isReceivable: boolean;
  isActive: boolean;
}

/**
 * Inventory balance categories (on-hand / held / damaged / …). Note the id is an
 * int here, not a Guid — it matches tbl_BalanceType.BalanceTypeID.
 */
export interface BalanceTypeDto {
  balanceTypeId: number;
  balanceTypeCode: string;
  displayName: string;
  /** Whether stock in this category counts as available to promise. */
  isAvailable: boolean;
  isActive: boolean;
}

/** A country. The code is the value stored on an address, not a display name. */
export interface CountryDto {
  countryCode: string;
  countryName: string;
  isActive: boolean;
}

/**
 * A state, province or equivalent.
 *
 * Carries its country because the code alone does not identify one — the codes repeat across
 * countries, which is why the list is fetched per country rather than whole.
 */
export interface StateProvinceDto {
  stateProvinceId: number;
  countryCode: string;
  stateProvinceCode: string;
  stateProvinceName: string;
  isActive: boolean;
}

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export async function listUoms(signal?: AbortSignal): Promise<UomDto[]> {
  const { data } = await apiRequest<UomDto[]>('/v1/uoms', {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function listItemTypes(signal?: AbortSignal): Promise<ItemTypeDto[]> {
  const { data } = await apiRequest<ItemTypeDto[]>('/v1/item-types', {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function listLocationTypes(signal?: AbortSignal): Promise<LocationTypeDto[]> {
  const { data } = await apiRequest<LocationTypeDto[]>('/v1/location-types', {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function listBalanceTypes(signal?: AbortSignal): Promise<BalanceTypeDto[]> {
  const { data } = await apiRequest<BalanceTypeDto[]>('/v1/balance-types', {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function listCountries(signal?: AbortSignal): Promise<CountryDto[]> {
  const { data } = await apiRequest<CountryDto[]>('/v1/countries', {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function listStateProvinces(
  countryCode: string,
  signal?: AbortSignal,
): Promise<StateProvinceDto[]> {
  const { data } = await apiRequest<StateProvinceDto[]>(
    `/v1/state-provinces?countryCode=${encodeURIComponent(countryCode)}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}
